import { Link, NavLink, Outlet } from "react-router";
import { NAV } from "../nav";
import { hasCapture, isRunning, useCapture } from "../lib/captureContext";
import { Icon } from "./Icons";

/** What Layout hands its pages through <Outlet context>. Settings holds Sign out. */
export interface LayoutContext {
  email: string | null;
  onSignOut: () => void;
}

/**
 * Chrome around every authenticated page: the wordmark and the gear, with the
 * tabs inline in the header from `sm` up and as an icon bar at the bottom of a
 * phone, where the thumb already is. `viewport-fit=cover` is set in index.html,
 * so the safe-area insets clear the notch and the home indicator.
 *
 * ONE <nav>, restyled per breakpoint, not two: jsdom applies no CSS, so a
 * hidden duplicate would still be a second "Credits" link to every test.
 * The header has no backdrop-filter because that would make it the containing
 * block for the nav's `fixed` phone position. Sign out lives in Settings
 * (restyle, 2026-09-12). `react-router`, never `react-router-dom`
 * (eslint.config.js enforces it).
 *
 * Refresh sits beside the gear and is drawn ONLY when the capture extension has
 * answered a handshake. Not a seventh tab: a tab that appears in one browser and
 * vanishes in another is worse than a control that is simply absent, and six tabs
 * already fill a 360px bottom bar. It is the whole gate -- no handshake, no
 * control, and the route redirects -- so there is never a dead button.
 * (2026-10-03)
 */
export function Layout({ email, onSignOut }: LayoutContext) {
  const capture = useCapture();
  const offerCapture = hasCapture(capture);
  const running = isRunning(capture);
  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-30 flex items-center gap-5 border-b border-neutral-800/70 bg-neutral-950 px-4 pt-[calc(0.5rem+env(safe-area-inset-top))] pb-2 sm:px-6">
        <Link to="/" className="wordmark text-[21px]">
          Wallet<span className="text-accent">.</span>
        </Link>
        <nav
          aria-label="Pages"
          className="fixed inset-x-0 bottom-0 z-40 grid grid-cols-6 border-t border-neutral-800/70 bg-neutral-950/95 pb-[env(safe-area-inset-bottom)] backdrop-blur
                     sm:static sm:flex sm:gap-0.5 sm:border-0 sm:bg-transparent sm:p-0 sm:backdrop-blur-none"
        >
          {NAV.map((entry) => (
            <NavLink
              key={entry.to}
              to={entry.to}
              // `end` on the index route only. Without it "/" stays active on
              // every path, because NavLink matches by prefix.
              end={entry.to === "/"}
              className={({ isActive }) =>
                `grid min-h-14 place-items-center content-center gap-0.5 text-[11px] sm:flex sm:min-h-0 sm:rounded-lg sm:px-3 sm:py-1.5 sm:text-sm ${
                  isActive
                    ? "text-neutral-100 sm:bg-neutral-800"
                    : "text-neutral-500 hover:text-neutral-200 sm:text-neutral-400"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon name={entry.icon} size={21} className={`sm:hidden ${isActive ? "text-accent" : ""}`} />
                  {entry.label}
                </>
              )}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-1">
          {offerCapture && (
            <NavLink
              to="/refresh"
              // The word from sm up, the icon alone on a phone -- where this is
              // never drawn anyway, since a phone loads no extensions. It stays
              // responsive because a narrow desktop window is the same layout.
              className={`refresh-ctl ${running ? "refresh-ctl-live" : ""}`}
            >
              <Icon name="capture" size={18} />
              <span className="hidden sm:inline">{running ? "Refreshing" : "Refresh"}</span>
              {running && <i className="refresh-dot" aria-hidden="true" />}
              {running && <span className="sr-only">a run is going</span>}
            </NavLink>
          )}
          <NavLink to="/settings" aria-label="Settings" className="icon-btn">
            <Icon name="gear" />
          </NavLink>
        </div>
      </header>

      {/* pb-28 clears the fixed bottom bar, which is out of flow and would
          otherwise cover the last item on every page. */}
      <main className="mx-auto w-full max-w-5xl flex-1 p-4 pb-28 sm:px-6 sm:pt-6 sm:pb-10">
        <Outlet context={{ email, onSignOut } satisfies LayoutContext} />
      </main>
    </div>
  );
}
